/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 24/08/22
 */

import WorkplanStateDisplay from '@/components/contexts/WorkplanContext/WorkplanStateDisplay';
import { useWorkplanSummary } from '@/components/contexts/WorkplanSummaryContext/useWorkplanSummary';
import { BottomTile } from '@/components/pages/dashboard/tiles';
import { Tile } from '@/components/Tile';
import { isWorkplanReadonly } from '@/utils/isWorkplanReadonly';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useWorkplanVersion from '@/components/contexts/VersionContext/useWorkplanVersion';
import { defaultActionLists, FormType } from '@/components/DefaultActionList/DefaultActionList';
import { Box, GridItem, Heading, Text } from '@chakra-ui/react';
import { useWorkplanCategories } from '@/components/contexts/WorkplanContext/useWorkplanCategories';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';
import { CommentsTile } from '@/components/pages/workplan/comment/CommentsTile';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import InfoTooltip from '@/components/Tooltip/InfoTooltip';
import { TrainingTileAdditions } from '@/components/pages/dashboard/tiles/ItemsTile/TrainingTileAdditions';
import StaticForm from '@/components/StaticForm/StaticForm';

const ItemsTile = () => {
    const { fetchSummary } = useWorkplanSummary();

    const { version, workplanVersion, current } = useWorkplanVersion();

    const navigate = useNavigate();
    const { makePath, admin, supervisor } = useNavigateOptions();

    const { currentAssignment } = useAssignment();

    const categories = useWorkplanCategories();

    return (
        <GridItem colSpan={2}>
            {categories.map((category, index) => {
                return (
                    <Box key={`box-${category.label}-${index}`} mt={index ? '1rem' : '0rem'} role="landmark">
                        <Heading
                            as="h2"
                            size={'md'}
                            color={'black'}
                            key={`heading-${category.label}-${index}`}
                            mb="1rem"
                        >
                            {`${index + 1}. ${
                                category.label === 'Training' ? 'Training and Development' : category.label
                            }`}
                            <InfoTooltip
                                tooltipLabel={`${category.label} tooltip`}
                                tooltipInfoText={category.helpText}
                            />
                        </Heading>
                        <Tile overflowX={'auto'} colspan={2} key={`Tile-${category.label}-${index}`}>
                            <WorkplanStateDisplay
                                render={(workplan) => {
                                    const isReadonly = !current || isWorkplanReadonly(workplan) || admin || supervisor;
                                    return (
                                        <>
                                            {category.label === 'Training' && (
                                                <TrainingTileAdditions isReadonly={isReadonly} />
                                            )}
                                            {category.endpoint && (
                                                <BottomTile
                                                    isReadOnly={isReadonly}
                                                    title={
                                                        category.label === 'Training'
                                                            ? 'Other Training and Development'
                                                            : undefined
                                                    }
                                                    buttonAction={
                                                        isReadonly
                                                            ? undefined
                                                            : {
                                                                  label: `Add ${category.addLabel}`,
                                                                  onClick: () => {
                                                                      if (workplanVersion)
                                                                          navigate(
                                                                              makePath(
                                                                                  workplan.id,
                                                                                  workplanVersion.version,
                                                                                  category.urlLabel,
                                                                              ),
                                                                          );
                                                                  },
                                                              }
                                                    }
                                                    getActions={(row, defaultActions, name) => {
                                                        const workplanItemName = name ? name : '';
                                                        return defaultActionLists(
                                                            row,
                                                            defaultActions,
                                                            workplan,
                                                            workplanItemName as FormType,
                                                            isReadonly,
                                                        );
                                                    }}
                                                    workplan={workplan}
                                                    version={version}
                                                    category={category}
                                                    onChange={() => fetchSummary()}
                                                />
                                            )}
                                            {category.template === 'behaviouralFormTemplateId' && (
                                                <StaticForm type={category.form} />
                                            )}
                                            {category.label === 'Training' && (
                                                <>
                                                    <Heading as="h3" size="sm" color="blue.500" marginTop={10}>
                                                        Training and Development Comments
                                                    </Heading>
                                                    <Text marginTop={1}>
                                                        Staff and supervisors can add comments relating to the progress
                                                        of their training and development in the space below during the
                                                        year.
                                                    </Text>
                                                </>
                                            )}
                                            {category.label !== 'Training' && (
                                                <Heading as="h3" size="sm" color="blue.500" marginTop={10}>
                                                    {category.label} Comments
                                                </Heading>
                                            )}
                                            <CommentsTile
                                                isReadonly={
                                                    !current || isWorkplanReadonly(workplan, supervisor || admin)
                                                }
                                                currentAssignment={currentAssignment}
                                                baseApiUrl={`workplan/${workplan.id}`}
                                                workplan={workplan}
                                                version={version}
                                                section={category.comments}
                                            />
                                        </>
                                    );
                                }}
                            />
                        </Tile>
                    </Box>
                );
            })}
        </GridItem>
    );
};

export default ItemsTile;
